import React from 'react';
import { Proposal } from '../../hooks/useProposals'; // Changed from Project
import Thermometer from './Thermometer'; // Path updated
import ProposalActionsMenu from './ProposalActionsMenu'; // Renamed from ProjectActionsMenu, path updated
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

// Placeholder Client interface
interface Client {
    id: string;
    name: string;
}

interface ProposalCardViewProps { // Renamed from ProjectCardViewProps
    proposals: Proposal[]; // Renamed from projects
    clients: Client[]; // Added
    loadingClients?: boolean; // Added
    formatDate: (dateString: string) => string;
    getTypeName: (typeId: string) => string;
    getTechnologyName: (techId: string) => string;
    getSeniorityName: (seniorityId: string) => string;
    handleEdit: (proposal: Proposal) => void; // Changed from project: Project
    handleDelete: (proposalId: string) => void; // Changed from projectId: string
}

export default function ProposalCardView({ // Renamed from ProjectCardView
    proposals, // Renamed
    clients,   // Added
    loadingClients, // Added
    formatDate,
    getTypeName,
    getTechnologyName,
    getSeniorityName,
    handleEdit,
    handleDelete
}: ProposalCardViewProps) {

    const getClientName = (clientId: string): string => {
        if (loadingClients) return 'Carregando cliente...';
        const client = clients.find(c => c.id === clientId);
        return client ? client.name : 'Cliente não encontrado';
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {proposals.map((proposal) => { // Renamed from project
                const clientName = getClientName(proposal.clientId);
                return (
                    <div key={proposal.id} className="bg-white shadow-lg rounded-lg overflow-hidden transform transition-all hover:scale-105">
                        <div className={`px-6 py-4 ${proposal.status === 'won' ? 'bg-green-50' : proposal.status === 'lost' ? 'bg-red-50' : proposal.status === 'cancelled' ? 'bg-gray-100' : 'bg-blue-50'}`}>
                            <div className="flex justify-between items-center">
                                <h4 className="text-lg font-semibold text-gray-800 truncate mr-2" title={proposal.name}>{proposal.name}</h4>
                                <div className="flex items-center">
                                    {proposal.proposalUrl && (
                                        <a
                                            href={proposal.proposalUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Baixar Proposta"
                                            className="mr-2 text-gray-500 hover:text-indigo-600"
                                        >
                                            <ArrowDownTrayIcon className="h-5 w-5" />
                                        </a>
                                    )}
                                    <div className={`px-3 py-1 rounded-full text-xs font-medium inline-block ${proposal.status === 'won' ? 'bg-green-100 text-green-800' :
                                        proposal.status === 'lost' ? 'bg-red-100 text-red-800' :
                                            proposal.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                                'bg-blue-100 text-blue-800'
                                        }`}>
                                        {proposal.status === 'won' ? 'Ganha' :      // Label updated to match proposal status
                                            proposal.status === 'lost' ? 'Perdida' :  // Label updated
                                                proposal.status === 'cancelled' ? 'Cancelada' : // Label updated
                                                    'Em Negociação'}
                                    </div>
                                </div>
                            </div>
                            {/* Display client name from lookup */}
                            <p className="text-sm text-gray-600 truncate" title={clientName}>{clientName}</p>
                            <p className="text-xs text-gray-500 mt-1">Responsável: {proposal.manager}</p>
                            <div className="mt-3">
                                {/* Pass heatLevel directly */}
                                <Thermometer heatLevel={proposal.heatLevel} mode="display" />
                            </div>
                        </div>
                        <div className="px-6 py-5 border-t border-gray-200">
                            <div className="flex justify-between items-center mb-3">
                                <span className="text-xs text-gray-500">Criado em: {proposal.createdAt ? formatDate(proposal.createdAt) : 'N/A'}</span>
                                <div className="ml-auto">
                                    <ProposalActionsMenu // Renamed from ProjectActionsMenu
                                        proposal={proposal} // Pass proposal object
                                        handleEdit={handleEdit}
                                        handleDelete={handleDelete}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <div className="flex items-center text-sm">
                                        <span className="font-medium text-gray-700 w-48">Duração:</span>
                                        <span className="text-gray-600">{proposal.duration}</span>
                                    </div>
                                    {proposal.expectedClosing && (
                                        <div className="flex items-center text-sm">
                                            <span className="font-medium text-gray-700 w-48">Expectativa de Fechamento:</span>
                                            <span className="text-gray-600">{formatDate(proposal.expectedClosing)}</span>
                                        </div>
                                    )}
                                    {proposal.proposalDate && (
                                        <div className="flex items-center text-sm">
                                            <span className="font-medium text-gray-700 w-48">Data da Proposta:</span>
                                            <span className="text-gray-600">{formatDate(proposal.proposalDate)}</span>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <h5 className="font-medium text-gray-700 mb-2">Recursos Necessários:</h5>
                                    {proposal.resources.length > 0 ? (
                                        <ul className="space-y-2">
                                            {proposal.resources.map((resource, idx) => (
                                                <li key={idx} className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
                                                    {resource.quantity} {getTypeName(resource.typeId)} {getTechnologyName(resource.technologyId)} • {getSeniorityName(resource.seniority)}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-gray-500">Nenhum recurso especificado.</p>
                                    )}
                                </div>
                            </div>
                            {proposal.notes && (
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-1">Observações:</h5>
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap bg-white py-3 rounded">
                                        {proposal.notes}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
} 